import React, { useEffect, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';

import CustomCheckBox from '../customCheckBox';
import SearchBox from '../searchBox';

import utils from '../../utils';
import './styles.scss';

/**
 *
 * @property {Object[]} items Items to be selected (requires at least id)
 * @property {Object[]} customSelectedItems Inject Custom selected items
 * @property {Number} listSize The size of items list
 * @property {Number} itemHeight The size of the item on the list list
 * @property {string} emptyMessage Message when the list is empty
 * @property {string} customSearchPlaceHolder Message on searchbox
 * @property {string} displayField Display (label) field on object
 * @property {string} valueField Value field on object
 * @property {string} sortField Sort items by field
 * @property {boolean} renderCheckbox Display checkbox?
 * @property {boolean} renderSearchbox Display searchbox?
 * @property {boolean} allSelected Set all items selected?
 * @property {boolean} hasCustomSelectedItems is using custom selected items?
 * @property {Function} renderActionButtons Display action buttons?
 * @property {Function} renderBulkActionButtons Display bulk action buttons?
 * @property {Function} onItemSelected Callback when an item is selected
 * @property {Function} onSelect Callback when selected items change
 * @property {Function} customRender Callback to render custom rows
 * @returns {JSX.Element}
 */

const MultiSelector = ({
  items,
  customSelectedItems = [],
  emptyMessage = 'No items to display',
  customSearchPlaceHolder = 'Search',
  listSize,
  itemHeight,
  displayField = 'id',
  valueField = 'id',
  sortField = 'id',
  renderCheckbox = true,
  renderSearchbox = true,
  allSelected = true,
  hasCustomSelectedItems = false,
  renderActionButtons,
  renderBulkActionButtons,
  onItemSelected,
  onSelect,
  customRender,
  filters = null
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set(allSelected ? items.map(i => i.id) : []));
  const [renderList, setRenderList] = useState(items);
  const [searchingBy, setSearchingBy] = useState('');
  const [searchedItems, setSearchedItems] = useState([]);

  const prevItemsRef = useRef();

  useEffect(() => {
    const sortedItems = [...items].sort(sortArray);
    if (!utils.compareArrays(prevItemsRef.current, sortedItems)) {
      setSelectedIds(new Set(allSelected ? sortedItems.map(i => i.id) : []));
      setSearchedItems([]);
      setSearchingBy('');
    }
    prevItemsRef.current = items;
  }, [items]);

  useEffect(() => {
    setRenderList(isSearching() ? searchedItems : items);
  }, [searchedItems, items]);

  useEffect(() => {
    if (onSelect) {
      const selected = items.filter(i => selectedIds.has(i.id));
      onSelect(selected);
    }
    setRenderList(isSearching() ? searchedItems : items);
  }, [selectedIds]);

  useEffect(() => {
    if (hasCustomSelectedItems) {
      const currentSelected = items.filter(i => selectedIds.has(i.id));
      const selectedHasChanged = !utils.compareArrays(customSelectedItems, currentSelected);
      if (selectedHasChanged) {
        setSelectedIds(new Set(customSelectedItems.map(i => i.id)));
      }
    }
  }, [customSelectedItems]);

  const isSearching = () => searchingBy !== '';

  const isSelected = itemId => selectedIds.has(itemId);

  const sortArray = (a, b) => utils.compareStrings(a[sortField], b[sortField]);

  // Toggle one or more items
  const toggleSelection = (itemsToToggle, remove) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      itemsToToggle.forEach(item => {
        remove ? next.delete(item.id) : next.add(item.id);
      });
      return next;
    });
  };

  // Select / deselect all (respects search scope)
  const toggleAll = isAllSelected => {
    const scope = isSearching() ? searchedItems : items;
    setSelectedIds(prev => {
      const next = new Set(prev);
      scope.forEach(item => {
        isAllSelected ? next.delete(item.id) : next.add(item.id);
      });
      return next;
    });
  };

  const onSearch = searchBy => {
    setSearchingBy(searchBy);
    if (searchBy !== '') {
      const localSearchedItems = items.filter(item => {
        const itemHas = option => item[option].toUpperCase().includes(searchBy.toUpperCase());
        return itemHas(displayField) || itemHas('id');
      });
      setSearchedItems(localSearchedItems);
      setRenderList(localSearchedItems);
    } else {
      setSearchedItems([]);
      setRenderList(items);
    }
  };

  const renderLabel = (item, label) => (customRender ? customRender(item) : label);

  const renderRow = ({ index, style }) => {
    const item = renderList[index];
    const value = item[valueField];
    const selected = isSelected(item.id);
    const label =
      renderList.length === 1 ? (
        <div id={item.id}>{item[displayField]}</div>
      ) : (
        utils.renderTrimmedString(item[displayField], item.id)
      );

    return (
      <div
        className={`item ${selected ? 'selected' : ''}`}
        key={item.id}
        style={style}
        onClick={() => {
          if (onItemSelected) {
            onItemSelected(value, item.id);
            toggleSelection([item], selected);
          }
        }}
      >
        {renderCheckbox ? (
          <CustomCheckBox
            selected={isSelected(item.id) ? 'all' : 'none'}
            label={renderLabel(item, label)}
            onClick={value => toggleSelection([item], value)}
            key={item.id}
          />
        ) : (
          renderLabel(item, label)
        )}
        {renderActionButtons ? renderActionButtons(item) : null}
      </div>
    );
  };

  const renderEmpty = () => (
    <div className="item-list no-devices">
      <p>{emptyMessage}</p>
    </div>
  );

  const selectedItems = items.filter(i => selectedIds.has(i.id));

  const renderItems = () => (
    <>
      {renderCheckbox && (
        <div className="toolbar">
          <CustomCheckBox
            multiple
            selectedItems={selectedItems.length}
            totalItems={renderList.length}
            label={`All - ${selectedItems.length} / ${items.length}`}
            onClick={toggleAll}
          />
          {renderBulkActionButtons ? renderBulkActionButtons(selectedItems) : null}
        </div>
      )}
      <div className="item-list">
        {isSearching() && searchedItems.length === 0 && <p>{emptyMessage}</p>}
        <List
          className={'device-virtual-list'}
          height={listSize ? listSize : window.innerHeight - 368}
          itemCount={renderList.length}
          itemSize={itemHeight ? itemHeight : 46}
          width="100%"
        >
          {renderRow}
        </List>
      </div>
    </>
  );

  return (
    <div className="multiselector">
      {renderSearchbox && (
        <div className="search-filter-bar">
          <SearchBox onChange={onSearch} placeholder={customSearchPlaceHolder} searchingBy={searchingBy} />
          {filters}
        </div>
      )}
      {items.length === 0 ? renderEmpty() : renderItems()}
    </div>
  );
};

export default MultiSelector;
